// CB:BEGIN admin-dispute-center
import React, { useState, useEffect, useCallback } from 'react';
import { useAuthFetch } from '../../lib/useAuthFetch';
import { getApiUrl } from '../../lib/getApiUrl';
import StatusBadge from '../../components/ui/StatusBadge';
import EmptyState from '../../components/ui/EmptyState';

interface Dispute {
  id: string;
  projectId: string;
  raisedByUserId: string;
  raisedByRole?: string;
  category?: string;
  description?: string;
  riskScore?: number;
  status: string;
  resolution?: string;
  escrowImpact?: string;
  milestoneKey?: string;
  createdAt: string;
  resolvedAt?: string;
  project?: { id: string; address: string; homeownerId?: string; contractorId?: string };
}

const OUTCOME_OPTIONS = [
  { value: 'RESOLVED_CONTRACTOR', label: 'In favor of Contractor' },
  { value: 'RESOLVED_HOMEOWNER',  label: 'In favor of Homeowner'  },
  { value: 'RESOLVED_SPLIT',      label: 'Split resolution'       },
];

const RISK_COLOR = (score?: number) => {
  if (!score) return '#94a3b8';
  if (score >= 7) return '#dc2626';
  if (score >= 4) return '#ea580c';
  return '#059669';
};

const RISK_BG = (score?: number) => {
  if (!score) return '#f1f5f9';
  if (score >= 7) return '#fef2f2';
  if (score >= 4) return '#fff7ed';
  return '#f0fdf4';
};

const FILTER_STATUSES = ['OPEN', 'ESCALATED', 'INVESTIGATING', 'RESOLVED', 'CLOSED'];

interface AdminDisputeCenterProps {
  onNavigate?: (screen: string) => void;
}

const AdminDisputeCenter: React.FC<AdminDisputeCenterProps> = ({ onNavigate }) => {
  const authFetch = useAuthFetch();

  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [resolution, setResolution] = useState('');
  const [outcome, setOutcome] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = getApiUrl(`/api/admin/disputes${statusFilter ? `?status=${statusFilter}` : ''}`);
      const res = await authFetch(url);
      if (res.ok) {
        const data = await res.json();
        setDisputes(data.disputes || []);
      }
    } catch (e) {
      console.error('[AdminDisputeCenter] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [authFetch, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const selected = disputes.find(d => d.id === selectedId) ?? null;

  const handleResolve = async () => {
    if (!selectedId || !outcome) {
      setResolveError('Select an outcome before resolving.');
      return;
    }
    setResolveError('');
    setResolving(true);
    try {
      const res = await authFetch(getApiUrl(`/api/admin/disputes/${selectedId}/resolve`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolution, outcome }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        setResolveError((e as any).error || 'Resolve failed');
        return;
      }
      setSelectedId(null);
      setResolution('');
      setOutcome('');
      await load();
    } catch {
      setResolveError('Network error — please try again.');
    } finally {
      setResolving(false);
    }
  };

  const openCount    = disputes.filter(d => ['OPEN','ESCALATED','INVESTIGATING'].includes(d.status)).length;
  const resolvedCount = disputes.filter(d => ['RESOLVED','CLOSED'].includes(d.status)).length;

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '9px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    fontSize: 14,
    color: '#111827',
    boxSizing: 'border-box',
    background: '#fff',
  };

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1100, margin: '0 auto', fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>Dispute Center</h1>
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Review and resolve project disputes across all homeowners and contractors.</p>
      </div>

      {/* ── Summary strip ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 28 }}>
        {[
          { label: 'Open / Active', value: openCount,    color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
          { label: 'Resolved',      value: resolvedCount, color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
          { label: 'Total',         value: disputes.length, color: '#374151', bg: '#f8fafc', border: '#e2e8f0' },
        ].map(c => (
          <div key={c.label} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 12, padding: '16px 20px' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: c.color, lineHeight: 1 }}>{c.value}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* ── Table card ── */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: 20 }}>

        {/* Table toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginRight: 'auto' }}>Disputes</span>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, color: '#374151', background: '#fff' }}
          >
            <option value="">All statuses</option>
            {FILTER_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</option>)}
          </select>
          <button
            onClick={() => load()}
            style={{ padding: '6px 14px', background: '#f9fafb', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12, cursor: 'pointer', color: '#374151', fontWeight: 500 }}
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>Loading disputes…</div>
        ) : disputes.length === 0 ? (
          <EmptyState
            title="No disputes found"
            description={statusFilter ? `No disputes with status "${statusFilter}".` : 'All disputes will appear here when raised.'}
          />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {['Project / Address', 'Category', 'Raised By', 'Risk', 'Status', 'Date', ''].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {disputes.map(d => (
                  <tr
                    key={d.id}
                    style={{ borderBottom: '1px solid #f3f4f6', background: selectedId === d.id ? '#f0fdf4' : '#fff', cursor: 'pointer', transition: 'background 0.1s' }}
                    onClick={() => setSelectedId(prev => prev === d.id ? null : d.id)}
                  >
                    <td style={{ padding: '12px 16px', maxWidth: 220 }}>
                      <div style={{ fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {d.project?.address || d.projectId}
                      </div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2, fontFamily: 'monospace' }}>#{d.projectId.slice(-8)}</div>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#374151' }}>{d.category || '—'}</td>
                    <td style={{ padding: '12px 16px', color: '#374151' }}>{d.raisedByRole || '—'}</td>
                    <td style={{ padding: '12px 16px' }}>
                      {d.riskScore != null ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, background: RISK_BG(d.riskScore), color: RISK_COLOR(d.riskScore), fontWeight: 700, fontSize: 13 }}>
                          {d.riskScore}
                        </span>
                      ) : <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <StatusBadge status={d.status} />
                    </td>
                    <td style={{ padding: '12px 16px', color: '#9ca3af', whiteSpace: 'nowrap', fontSize: 12 }}>
                      {new Date(d.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {onNavigate && (
                        <button
                          onClick={e => { e.stopPropagation(); onNavigate(`admin-project-edit-${d.projectId}`); }}
                          style={{ padding: '5px 12px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#374151', fontWeight: 500 }}
                        >
                          View project
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Resolve panel ── */}
      {selected && !['RESOLVED', 'CLOSED'].includes(selected.status) && (
        <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '24px 28px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>Resolve Dispute</h3>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                {selected.project?.address || selected.projectId}
                {' · '}
                <StatusBadge status={selected.status} />
              </div>
            </div>
            <button
              onClick={() => { setSelectedId(null); setResolveError(''); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 20, lineHeight: 1 }}
            >
              ×
            </button>
          </div>

          {selected.description && (
            <div style={{ padding: '12px 16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#374151', lineHeight: 1.6, marginBottom: 20 }}>
              {selected.description}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 640, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Outcome <span style={{ color: '#dc2626' }}>*</span></label>
              <select value={outcome} onChange={e => setOutcome(e.target.value)} style={inputStyle}>
                <option value="">Select outcome…</option>
                {OUTCOME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Resolution Notes</label>
              <textarea
                value={resolution}
                onChange={e => setResolution(e.target.value)}
                placeholder="Add context for the audit log…"
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>
          </div>

          {resolveError && (
            <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13, marginBottom: 14 }}>
              {resolveError}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={handleResolve}
              disabled={resolving || !outcome}
              style={{ padding: '10px 22px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: resolving || !outcome ? 'not-allowed' : 'pointer', opacity: resolving || !outcome ? 0.6 : 1 }}
            >
              {resolving ? 'Resolving…' : 'Mark Resolved'}
            </button>
            <button
              onClick={() => { setSelectedId(null); setResolveError(''); }}
              style={{ padding: '10px 20px', background: '#f9fafb', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontWeight: 500 }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDisputeCenter;
// CB:END admin-dispute-center
